import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import type { ClassworkCorrectionRow, RubricDiagnostics, RubricPayload } from '../../lib/classworkApi';
import {
  fetchClassworkCorrectionDebug,
  fetchClassworkCorrections,
  getReferenceVersionRubric,
  getReferenceVersionRubricDiagnostics,
  rebuildReferenceVersionRubric
} from '../../lib/classworkApi';

function formatLlmField(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') {
    const t = v.trim();
    if ((t.startsWith('[') || t.startsWith('{')) && t.length > 1) {
      try {
        return JSON.stringify(JSON.parse(t), null, 2);
      } catch {
        return v;
      }
    }
    return v;
  }
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function parseLlmJson(value: unknown): any {
  if (value == null) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  const t = value.trim();
  if (!t) return null;
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}

function modelNameFromRow(row: ClassworkCorrectionRow): string | null {
  const direct = String(row?.model_name || '').trim();
  if (direct) return direct;
  const parsed = parseLlmJson(row?.llm_response_json);
  const fromPayload = String(parsed?.model || '').trim();
  return fromPayload || null;
}

function parseJsonLike(value: unknown): any {
  if (value == null) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

type RubricTaskScore = {
  id: string;
  label: string;
  pointsMax: number;
  pointsAchieved: number;
  bonus?: boolean;
  rationale?: string | null;
  missing?: boolean;
  evidenceQuote?: string | null;
};

type RubricComputation = {
  pointsAchievedSum?: number;
  pointsMaxBaseSum?: number;
  pointsMaxBonusSum?: number;
  pointsAchievedBonusSum?: number;
  missingTaskIds?: string[];
  unexpectedTaskIds?: string[];
  rubricSource?: string | null;
  formula?: string;
  /**
   * Vom Server gesetzt, wenn die LLM-Antwort in sich widersprüchlich war
   * (z. B. alle Aufgaben missing=true, aber strengths/issues nennen konkrete
   * Schülerlösungen). Teacher bekommt dann in der UI einen deutlichen Hinweis.
   */
  consistencyWarning?: string | null;
  /**
   * Wenn die erste LLM-Antwort widersprüchlich war, startet der Server einen gezielten
   * Repair-Call (zweiter Pass, der nur task_scores neu vergibt). Hier steht, ob das geklappt hat.
   */
  repair?: {
    applied: boolean;
    previousPointsAchievedSum?: number;
    repairedPointsAchievedSum?: number;
    reason?: string;
    rawResponseSnippet?: string | null;
  } | null;
  /**
   * Widerspruchs-Detektor (Maßnahme 3): Aufgabe, die Vision als sichtbar leer erkannt hat,
   * wurde von der KI trotzdem als bearbeitet gewertet, ohne ein wortwörtliches Handschrift-Zitat
   * liefern zu können. Wenn gesetzt, wird Auto-Note zurückgestellt.
   */
  conflictTaskIds?: string[];
  emptyTaskHints?: string[];
  reviewBlockReason?: string | null;
};

const PIPELINE_REASON_GLOSSARY: Record<string, { label: string; hint: string }> = {
  OK: {
    label: 'Alles stabil',
    hint: 'Die automatischen Signale sind konsistent genug für eine sichere Entscheidung.'
  },
  LOW_OCR: {
    label: 'Niedrige OCR-Sicherheit',
    hint: 'Der erkannte Text ist unsicher; Antworten sollten manuell gegengeprüft werden.'
  },
  ALIGNMENT_AMBIGUOUS: {
    label: 'Aufgaben-Zuordnung unklar',
    hint: 'Antworten konnten nicht eindeutig Aufgaben zugeordnet werden.'
  },
  SCORER_DIVERGENCE: {
    label: 'Bewertungs-Konflikt',
    hint: 'Regel- und KI-Scoring weichen deutlich voneinander ab.'
  },
  REPAIR_APPLIED: {
    label: 'Reparaturlauf aktiv',
    hint: 'Die Pipeline hat einen zusätzlichen Korrekturlauf ausgeführt.'
  },
  REVIEW_REQUIRED: {
    label: 'Manuelle Prüfung nötig',
    hint: 'Die automatische Veröffentlichung wurde bewusst blockiert.'
  },
  VISION_UNAVAILABLE: {
    label: 'Bildanalyse ausgefallen',
    hint: 'Die Entscheidung basiert nur eingeschränkt auf visuellem Kontext.'
  },
  SUBMISSION_CROP_UNREADABLE: {
    label: 'Task-Crops nicht lesbar',
    hint: 'Erwartete Antwortregionen wurden gefunden, aber es konnte kein belastbarer Text gelesen werden.'
  },
  CROP_TEXT_LOW_CONFIDENCE: {
    label: 'Crop-Text unsicher',
    hint: 'Die Crop-Pipeline hat Text erkannt, aber mit niedriger Sicherheit.'
  },
  WHOLE_PAGE_FALLBACK_USED: {
    label: 'Whole-Page-Fallback',
    hint: 'Die Bewertung lief ohne aktive Crop-Pipeline über den klassischen Seitenflow.'
  }
};

function formatPoints(value: unknown): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return Number.isInteger(n) ? String(n) : (Math.round(n * 100) / 100).toLocaleString('de-DE');
}

function extractRubricFromLlmJson(llmJson: any): {
  tasks: RubricTaskScore[];
  computation: RubricComputation | null;
} {
  const rawTasks = Array.isArray(llmJson?.task_scores)
    ? llmJson.task_scores
    : Array.isArray(llmJson?.taskScores)
      ? llmJson.taskScores
      : [];
  const tasks: RubricTaskScore[] = rawTasks
    .map((t: any, idx: number): RubricTaskScore | null => {
      if (!t || typeof t !== 'object') return null;
      const pointsMax = Number(t.pointsMax ?? t.points_max ?? t.max ?? 0);
      const pointsAchieved = Number(
        t.pointsAchieved ?? t.points_achieved ?? t.achieved ?? 0
      );
      if (!Number.isFinite(pointsMax) || pointsMax <= 0) return null;
      const evidenceQuoteRaw =
        t.evidenceQuote ?? t.evidence_quote ?? t.evidence ?? t.zitat ?? t.beleg ?? null;
      const evidenceQuote =
        typeof evidenceQuoteRaw === 'string' && evidenceQuoteRaw.trim()
          ? evidenceQuoteRaw.trim()
          : null;
      return {
        id: String(t.id ?? `task_${idx + 1}`),
        label: String(t.label ?? t.title ?? `Aufgabe ${idx + 1}`),
        pointsMax,
        pointsAchieved: Number.isFinite(pointsAchieved) ? pointsAchieved : 0,
        bonus: Boolean(t.bonus),
        rationale:
          typeof t.rationale === 'string' && t.rationale.trim()
            ? t.rationale.trim()
            : null,
        missing: Boolean(t.missing),
        evidenceQuote
      };
    })
    .filter((x: RubricTaskScore | null): x is RubricTaskScore => x != null);
  const computationRaw = llmJson?.rubric_computation || llmJson?.rubricComputation || null;
  return {
    tasks,
    computation: computationRaw && typeof computationRaw === 'object' ? (computationRaw as RubricComputation) : null
  };
}

type Props = {
  classworkId: number;
  assignmentId: number | null;
  /** z. B. „Müller, Anna · 7c“ */
  subtitle?: string;
  /** Persistierte Zeile aus classwork_grades (Auto-Note nach LLM, sofern Notenschema gesetzt) */
  persistedGradeRow?: { grade_value?: string | null; score_percent?: number | string | null } | null;
  onRerunCorrection?: (assignmentId: number) => void | Promise<void>;
  onRestoreCorrection?: (assignmentId: number, correctionId: number) => void | Promise<void>;
};

export default function ClassworkLlmCorrectionView({
  classworkId,
  assignmentId,
  subtitle,
  persistedGradeRow,
  onRerunCorrection,
  onRestoreCorrection
}: Props) {
  const [viewMode, setViewMode] = useState<'simple' | 'detail'>('simple');
  const [busyAction, setBusyAction] = useState<'rerun' | `restore:${number}` | null>(null);
  const [selectedArchivedCorrectionId, setSelectedArchivedCorrectionId] = useState<number | null>(null);
  const q = useQuery(
    ['classwork-corrections', classworkId, assignmentId],
    () => fetchClassworkCorrections(classworkId, assignmentId!),
    { enabled: Boolean(classworkId && assignmentId), staleTime: 15_000 }
  );
  const debugQuery = useQuery(
    ['classwork-corrections-debug', classworkId, assignmentId],
    () => fetchClassworkCorrectionDebug(classworkId, assignmentId!),
    { enabled: Boolean(classworkId && assignmentId), staleTime: 15_000 }
  );

  if (!assignmentId) return null;

  const rows = (q.data || []) as ClassworkCorrectionRow[];
  const activeRows = rows.filter((r) => Number(r?.is_archived || 0) !== 1);
  const archivedRows = rows.filter((r) => Number(r?.is_archived || 0) === 1);
  const latestCompleted = activeRows.find((r) => String(r.status) === 'completed');
  const latestAny = activeRows[0] || rows[0];
  const hasAnyActive = activeRows.length > 0;
  const hasRunningActive = activeRows.some((r) => ['queued', 'processing'].includes(String(r?.status || '').toLowerCase()));
  const canRerun = Boolean(assignmentId && hasAnyActive && !hasRunningActive && onRerunCorrection);
  const selectedArchivedRow =
    archivedRows.find((r) => Number(r.id) === Number(selectedArchivedCorrectionId)) || archivedRows[0] || null;

  const debugLogsByCorrectionId = (debugQuery.data?.debugLogsByCorrectionId || {}) as Record<string, any>;

  // Referenz-Raster für diese Korrektur nachladen: falls die KI keine `task_scores`
  // geliefert hat (z. B. bei alten Korrekturläufen vor Einführung des Rasters), zeigen wir
  // trotzdem das Raster als Skelett, damit die Lehrkraft sieht, welches Schema gilt.
  const primaryReferenceSetVersionId = useMemo(() => {
    const fromRow =
      latestCompleted?.reference_set_version_id ?? latestAny?.reference_set_version_id ?? null;
    const n = Number(fromRow);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [latestCompleted?.reference_set_version_id, latestAny?.reference_set_version_id]);

  const referenceRubricQuery = useQuery(
    ['classwork-reference-rubric', classworkId, primaryReferenceSetVersionId],
    () => getReferenceVersionRubric(classworkId, Number(primaryReferenceSetVersionId)),
    { enabled: Boolean(classworkId && primaryReferenceSetVersionId), staleTime: 30_000 }
  );
  const referenceRubric: RubricPayload | null = referenceRubricQuery.data?.rubric ?? null;

  // Diagnose zur Raster-Ableitung. Wird nur angefordert, wenn wir noch kein strukturiertes
  // Raster haben – dann wollen wir der Lehrkraft konkret zeigen, was schiefgelaufen ist
  // (Status, letzter Fehler, vorhandene Artefakte) und per Klick „Raster jetzt ableiten".
  const referenceRubricDiagnosticsQuery = useQuery(
    ['classwork-reference-rubric-diag', classworkId, primaryReferenceSetVersionId],
    () => getReferenceVersionRubricDiagnostics(classworkId, Number(primaryReferenceSetVersionId)),
    {
      enabled:
        Boolean(classworkId && primaryReferenceSetVersionId) &&
        !(referenceRubric && referenceRubric.tasks?.length),
      staleTime: 15_000
    }
  );
  const referenceRubricDiag: RubricDiagnostics | null = referenceRubricDiagnosticsQuery.data ?? null;

  const queryClient = useQueryClient();
  const [rebuildError, setRebuildError] = useState<string | null>(null);
  const [rebuildLastReason, setRebuildLastReason] = useState<string | null>(null);
  const rubricRebuildMutation = useMutation(
    async (variables: { forceMode?: 'autogen' } = {}) => {
      if (!primaryReferenceSetVersionId) throw new Error('no_reference_version');
      return rebuildReferenceVersionRubric(
        classworkId,
        Number(primaryReferenceSetVersionId),
        variables.forceMode ? { forceMode: variables.forceMode } : undefined
      );
    },
    {
      onMutate: () => {
        setRebuildError(null);
        setRebuildLastReason(null);
      },
      onSuccess: (data) => {
        setRebuildLastReason(data?.rebuild?.status || null);
        if (data?.rebuild?.status === 'failed') {
          setRebuildError(data.rebuild.error || 'Raster-Ableitung fehlgeschlagen.');
        }
        queryClient.invalidateQueries([
          'classwork-reference-rubric',
          classworkId,
          primaryReferenceSetVersionId
        ]);
        queryClient.invalidateQueries([
          'classwork-reference-rubric-diag',
          classworkId,
          primaryReferenceSetVersionId
        ]);
      },
      onError: (err: any) => {
        setRebuildError(String(err?.response?.data?.error || err?.message || 'Fehler'));
      }
    }
  );

  const renderBody = (
    row: ClassworkCorrectionRow,
    mode: 'simple' | 'detail',
    opts: { showPersistedGrade?: boolean } = {}
  ) => {
    const { showPersistedGrade = true } = opts;
    const pct = row.score_percent != null ? Number(row.score_percent) : null;
    const unc = row.uncertainty != null ? Number(row.uncertainty) : null;
    const modelDisplayName = modelNameFromRow(row);
    const pgv = String(persistedGradeRow?.grade_value ?? '').trim();
    const pgPctRaw = persistedGradeRow?.score_percent;
    const pgPct =
      pgPctRaw != null && pgPctRaw !== '' && !Number.isNaN(Number(pgPctRaw)) ? Number(pgPctRaw) : null;
    /* Maßnahme 3: Wenn der Server einen Widerspruch zwischen Vision(leer) und task_scores(nicht missing)
       gemeldet hat, blenden wir die Auto-Note aus und zeigen stattdessen einen Review-Hinweis. Früh
       aus llmJson extrahiert, damit die Kachel-Rendering-Logik weiter unten reagieren kann. */
    const llmJson = parseLlmJson(row.llm_response_json);
    const reviewBlockReason =
      typeof llmJson?.rubric_computation?.reviewBlockReason === 'string' &&
      llmJson.rubric_computation.reviewBlockReason.trim()
        ? String(llmJson.rubric_computation.reviewBlockReason).trim()
        : null;
    const reviewBlockConflictIds: string[] = Array.isArray(
      llmJson?.rubric_computation?.conflictTaskIds
    )
      ? llmJson.rubric_computation.conflictTaskIds.map((x: unknown) => String(x))
      : [];
    const canShowPersistedGrade =
      showPersistedGrade &&
      !reviewBlockReason &&
      persistedGradeRow &&
      ((pgv && pgv.toLowerCase() !== 'n/a') || (pgPct != null && Number.isFinite(pgPct)));
    const st = formatLlmField(row.strengths);
    const iss = formatLlmField(row.issues);
    const sug = formatLlmField(row.suggestions);
    const studentFriendlyStatus = String(row.student_friendly_status || 'idle').toLowerCase();
    const studentFriendlyText = String(row.student_friendly_text || '').trim();
    const studentFriendlyError = String(row.student_friendly_error || '').trim();
    const debugForRow = debugLogsByCorrectionId[String(Number(row?.id || 0))]?.correction || null;
    const promptRaw = debugForRow?.prompt_json ?? null;
    const promptObj = parseJsonLike(promptRaw);
    const promptPretty = promptObj ? JSON.stringify(promptObj, null, 2) : formatLlmField(promptRaw);
    const promptMessages = Array.isArray(promptObj?.messages) ? promptObj.messages : [];
    const systemPrompt = promptMessages.find((m: any) => String(m?.role || '').toLowerCase() === 'system')?.content;
    const userPrompt = promptMessages.find((m: any) => String(m?.role || '').toLowerCase() === 'user')?.content;
    const userPayloadObj = parseJsonLike(userPrompt);
    const userPayloadPretty = userPayloadObj
      ? JSON.stringify(userPayloadObj, null, 2)
      : typeof userPrompt === 'string'
        ? userPrompt
        : formatLlmField(userPrompt);
    const systemPromptPretty =
      typeof systemPrompt === 'string' ? systemPrompt : formatLlmField(systemPrompt);
    const competencyAssessment = Array.isArray(llmJson?.competency_assessment) ? llmJson.competency_assessment : [];
    const { tasks: rubricTasksFromLlm, computation: rubricComputation } = extractRubricFromLlmJson(llmJson);
    const pipelineDecision = llmJson?.pipelineDecision && typeof llmJson.pipelineDecision === 'object'
      ? llmJson.pipelineDecision
      : null;
    const pipelineReasonCode = String(
      pipelineDecision?.reasonCode || (reviewBlockReason ? 'REVIEW_REQUIRED' : 'OK')
    )
      .trim()
      .toUpperCase();
    const pipelineGlossary = PIPELINE_REASON_GLOSSARY[pipelineReasonCode] || PIPELINE_REASON_GLOSSARY.REVIEW_REQUIRED;
    const pipelineReasonCodes: string[] = Array.isArray(pipelineDecision?.reasonCodes)
      ? pipelineDecision.reasonCodes.map((x: unknown) => String(x).trim().toUpperCase()).filter(Boolean)
      : [pipelineReasonCode];
    const decisionTimeline = [
      {
        stage: 'Vision/OCR',
        value:
          pipelineDecision?.ocrConfidence != null
            ? `${Math.round(Number(pipelineDecision.ocrConfidence) * 100)}%`
            : 'n/a'
      },
      {
        stage: 'Aufgaben-Alignment',
        value:
          pipelineDecision?.alignmentConfidence != null
            ? `${Math.round(Number(pipelineDecision.alignmentConfidence) * 100)}%`
            : 'n/a'
      },
      {
        stage: 'Task-Crops',
        value:
          pipelineDecision?.cropStats?.enabled === true
            ? `${Number(pipelineDecision?.cropStats?.matchedRegions || 0)} Treffer`
            : 'Fallback'
      },
      {
        stage: 'Policy-Entscheidung',
        value: String(pipelineDecision?.decision || (reviewBlockReason ? 'HUMAN_REVIEW' : 'AUTO_GRADE'))
      }
    ];

    // Fallback: Wenn die Korrektur keine `task_scores` enthält (z. B. alte Korrekturläufe),
    // aber zu dieser Referenzversion ein normalisiertes Raster vorliegt, zeigen wir wenigstens
    // das Raster-Skelett mit leeren Schülerpunkten an. Matched wird über reference_set_version_id,
    // damit für archivierte Zeilen mit anderer Referenzversion nichts fälsch zugeordnet wird.
    const rowRefVersionId = Number(row.reference_set_version_id);
    const referenceMatches =
      Number.isFinite(rowRefVersionId) &&
      rowRefVersionId > 0 &&
      rowRefVersionId === primaryReferenceSetVersionId;
    const rubricSkeletonTasks: RubricTaskScore[] =
      rubricTasksFromLlm.length === 0 && referenceMatches && referenceRubric?.tasks?.length
        ? referenceRubric.tasks.map((t) => ({
            id: String(t.id),
            label: String(t.label),
            pointsMax: Number(t.pointsMax) || 0,
            pointsAchieved: 0,
            bonus: Boolean(t.bonus),
            rationale: null,
            missing: true
          }))
        : [];
    const rubricTasks = rubricTasksFromLlm.length > 0 ? rubricTasksFromLlm : rubricSkeletonTasks;
    const rubricIsSkeletonOnly = rubricTasksFromLlm.length === 0 && rubricSkeletonTasks.length > 0;
    const rubricSkeletonSource = rubricIsSkeletonOnly ? referenceRubric?.source || null : null;
    const hasRubric = rubricTasks.length > 0;
    const rubricTotalAchieved =
      rubricComputation?.pointsAchievedSum != null
        ? Number(rubricComputation.pointsAchievedSum)
        : rubricTasks.reduce((sum, t) => sum + (Number(t.pointsAchieved) || 0), 0);
    const rubricTotalBase =
      rubricComputation?.pointsMaxBaseSum != null
        ? Number(rubricComputation.pointsMaxBaseSum)
        : rubricTasks.filter((t) => !t.bonus).reduce((sum, t) => sum + (Number(t.pointsMax) || 0), 0);
    const rubricBonusMax =
      rubricComputation?.pointsMaxBonusSum != null
        ? Number(rubricComputation.pointsMaxBonusSum)
        : rubricTasks.filter((t) => t.bonus).reduce((sum, t) => sum + (Number(t.pointsMax) || 0), 0);
    const rubricBonusAchieved =
      rubricComputation?.pointsAchievedBonusSum != null
        ? Number(rubricComputation.pointsAchievedBonusSum)
        : rubricTasks.filter((t) => t.bonus).reduce((sum, t) => sum + (Number(t.pointsAchieved) || 0), 0);
    return (
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap gap-3 text-xs text-gray-600">
          {modelDisplayName ? (
            <span>
              Modell: <span className="font-medium text-gray-800">{modelDisplayName}</span>
            </span>
          ) : null}
          {row.reference_set_version_id != null ? (
            <span>
              Referenz-Version: <span className="font-medium text-gray-800">{row.reference_set_version_id}</span>
            </span>
          ) : null}
          {row.completed_at ? (
            <span>
              Fertig: <span className="font-medium text-gray-800">{new Date(row.completed_at).toLocaleString('de-DE')}</span>
            </span>
          ) : null}
        </div>
        {reviewBlockReason ? (
          <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">
            <div className="font-semibold">KI-Korrektur zurueckgestellt – manuelle Pruefung erforderlich</div>
            <div className="mt-1 text-[12px] text-red-900/90">
              Die Bildanalyse hat {reviewBlockConflictIds.length > 0 ? reviewBlockConflictIds.length : 'mindestens eine'}{' '}
              {reviewBlockConflictIds.length === 1 ? 'Aufgabe' : 'Aufgaben'} als leer erkannt, der
              Korrektur-LLM hat dort aber trotzdem Punkte vergeben und konnte kein wortwörtliches
              Zitat aus der Schüler-Handschrift belegen. Deshalb wurde die Auto-Note{' '}
              <strong>nicht gesetzt</strong>. Bitte die entsprechenden Aufgaben manuell prüfen.
              {reviewBlockConflictIds.length > 0 ? (
                <>
                  {' '}
                  Betroffene Aufgaben-IDs: <code>{reviewBlockConflictIds.join(', ')}</code>.
                </>
              ) : null}
            </div>
          </div>
        ) : null}
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">
              Decision Timeline
            </div>
            <div className="text-xs text-slate-700">
              Reason: <span className="font-semibold">{pipelineGlossary.label}</span>
            </div>
          </div>
          <div className="mt-1 text-[12px] text-slate-700">{pipelineGlossary.hint}</div>
          <div className="mt-2 grid grid-cols-1 gap-1 text-[12px] text-slate-800 md:grid-cols-4">
            {decisionTimeline.map((item) => (
              <div key={item.stage} className="rounded border border-slate-200 bg-white px-2 py-1">
                <div className="text-[11px] text-slate-500">{item.stage}</div>
                <div className="font-semibold">{item.value}</div>
              </div>
            ))}
          </div>
          {pipelineReasonCodes.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {pipelineReasonCodes.map((code) => (
                <span key={code} className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] text-slate-700">
                  {code}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-4">
          {canShowPersistedGrade ? (
            <div className="rounded-lg bg-indigo-50 border border-indigo-200 px-3 py-2">
              <div className="text-xs text-indigo-800">Note (Notenschema, gespeichert)</div>
              <div className="text-lg font-semibold text-indigo-950">
                {pgv && pgv.toLowerCase() !== 'n/a' ? pgv : null}
                {pgv && pgv.toLowerCase() !== 'n/a' && pgPct != null ? ' · ' : null}
                {pgPct != null ? `${Math.round(pgPct)}%` : null}
              </div>
            </div>
          ) : reviewBlockReason && showPersistedGrade ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
              <div className="text-xs text-red-800">Note (Notenschema)</div>
              <div className="text-sm font-semibold text-red-900">
                Auto-Note nicht gesetzt – Review erforderlich
              </div>
            </div>
          ) : null}
          {pct != null && !Number.isNaN(pct) ? (
            <div className="rounded-lg bg-primary-50 border border-primary-100 px-3 py-2">
              <div className="text-xs text-primary-700">Vorschlag Prozent</div>
              <div className="text-lg font-semibold text-primary-900">{Math.round(pct)}%</div>
            </div>
          ) : null}
          {unc != null && !Number.isNaN(unc) ? (
            <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
              <div className="text-xs text-amber-800">Unsicherheit (KI)</div>
              <div className="text-lg font-semibold text-amber-900">{unc}</div>
            </div>
          ) : null}
        </div>
        {hasRubric ? (
          <div
            className={`rounded-lg border ${
              rubricIsSkeletonOnly ? 'border-amber-300 bg-amber-50/60' : 'border-emerald-200 bg-emerald-50/40'
            }`}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2 px-3 pt-3">
              <h4
                className={`text-xs font-semibold uppercase tracking-wide ${
                  rubricIsSkeletonOnly ? 'text-amber-900' : 'text-emerald-900'
                }`}
              >
                {rubricIsSkeletonOnly ? 'Korrekturraster (noch nicht ausgefüllt)' : 'Ausgefülltes Korrekturraster'}
              </h4>
              <div className={`text-xs ${rubricIsSkeletonOnly ? 'text-amber-900' : 'text-emerald-900'}`}>
                {rubricIsSkeletonOnly ? (
                  <span>
                    Max. Pflicht:{' '}
                    <span className="font-semibold">{formatPoints(rubricTotalBase)} P</span>
                    {rubricBonusMax > 0 ? (
                      <>
                        {' · Bonus: '}
                        <span className="font-semibold">{formatPoints(rubricBonusMax)} P</span>
                      </>
                    ) : null}
                  </span>
                ) : (
                  <>
                    Erreicht:{' '}
                    <span className="font-semibold">
                      {formatPoints(rubricTotalAchieved)}/{formatPoints(rubricTotalBase)}
                    </span>
                    {rubricBonusMax > 0 ? (
                      <>
                        {' · Bonus: '}
                        <span className="font-semibold">
                          +{formatPoints(rubricBonusAchieved)} / {formatPoints(rubricBonusMax)} mögl.
                        </span>
                      </>
                    ) : null}
                    {rubricTotalBase > 0 ? (
                      <>
                        {' · '}
                        <span className="font-semibold">
                          {Math.round((rubricTotalAchieved / rubricTotalBase) * 1000) / 10}%
                        </span>
                      </>
                    ) : null}
                  </>
                )}
              </div>
            </div>
            {rubricIsSkeletonOnly ? (
              <div className="mx-3 mt-2 rounded-md border border-amber-300 bg-amber-100/70 px-3 py-2 text-[12px] text-amber-900">
                <div>
                  Diese Korrektur wurde erstellt, <em>bevor</em> das Raster rasterbasiert ausgewertet wurde. Angezeigt ist
                  daher das <strong>Raster-Skelett</strong> (Aufgaben + maximale Punkte), aber noch keine Punkte pro Aufgabe
                  für diese Schülerarbeit.
                </div>
                {onRerunCorrection && assignmentId && canRerun ? (
                  <button
                    type="button"
                    className="mt-2 rounded border border-amber-500 bg-white px-2 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-50"
                    disabled={busyAction === 'rerun'}
                    onClick={async () => {
                      try {
                        setBusyAction('rerun');
                        await onRerunCorrection(assignmentId);
                      } finally {
                        setBusyAction(null);
                      }
                    }}
                  >
                    {busyAction === 'rerun' ? 'Starte neu …' : 'Jetzt neu korrigieren (Punkte pro Aufgabe)'}
                  </button>
                ) : null}
              </div>
            ) : null}
            <div className="px-3 pb-3 pt-2 overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-emerald-900/80">
                    <th className="pr-3 pb-1 font-semibold">Aufgabe</th>
                    <th className="pr-3 pb-1 font-semibold whitespace-nowrap">Punkte</th>
                    <th className="pr-3 pb-1 font-semibold">Typ</th>
                    {mode === 'detail' ? (
                      <th className="pb-1 font-semibold">Begründung</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {rubricTasks.map((t) => {
                    const ratio = Number(t.pointsMax) > 0 ? Number(t.pointsAchieved) / Number(t.pointsMax) : 0;
                    const rowTone = rubricIsSkeletonOnly
                      ? 'bg-white/60'
                      : t.missing
                        ? 'bg-amber-50'
                        : ratio >= 0.8
                          ? 'bg-emerald-50/60'
                          : ratio >= 0.5
                            ? 'bg-yellow-50/40'
                            : 'bg-red-50/40';
                    return (
                      <tr
                        key={`${t.id}-${t.label}`}
                        className={`border-t border-emerald-100 align-top ${rowTone}`}
                      >
                        <td className="py-1.5 pr-3 font-medium text-gray-900">
                          <span className="inline-block">{t.label}</span>
                          <span className="ml-1 text-[10px] text-gray-500">({t.id})</span>
                          {!rubricIsSkeletonOnly && t.missing ? (
                            <span className="ml-2 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900">
                              nicht bewertet
                            </span>
                          ) : null}
                        </td>
                        <td className="py-1.5 pr-3 whitespace-nowrap text-gray-900">
                          {rubricIsSkeletonOnly ? (
                            <>
                              <span className="font-semibold text-gray-400">–</span>
                              <span className="text-gray-500"> / {formatPoints(t.pointsMax)}</span>
                            </>
                          ) : (
                            <>
                              <span className="font-semibold">{formatPoints(t.pointsAchieved)}</span>
                              <span className="text-gray-500"> / {formatPoints(t.pointsMax)}</span>
                            </>
                          )}
                        </td>
                        <td className="py-1.5 pr-3">
                          {t.bonus ? (
                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900">
                              Bonus
                            </span>
                          ) : (
                            <span className="text-[10px] text-gray-500">Pflicht</span>
                          )}
                        </td>
                        {mode === 'detail' ? (
                          <td className="py-1.5 text-gray-700">
                            {t.rationale ||
                              (rubricIsSkeletonOnly
                                ? '—'
                                : t.missing
                                  ? 'Die KI hat zu dieser Aufgabe keine Punkte vergeben.'
                                  : '—')}
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {rubricComputation?.rubricSource || rubricComputation?.formula ? (
                <p className="mt-2 text-[11px] text-emerald-900/70">
                  {rubricComputation?.rubricSource ? (
                    <>Raster-Quelle: {rubricComputation.rubricSource}. </>
                  ) : null}
                  {rubricComputation?.formula || null}
                </p>
              ) : rubricSkeletonSource ? (
                <p className="mt-2 text-[11px] text-amber-900/80">Raster-Quelle: {rubricSkeletonSource}.</p>
              ) : null}
              {Array.isArray(rubricComputation?.unexpectedTaskIds) && rubricComputation!.unexpectedTaskIds!.length > 0 ? (
                <p className="mt-1 text-[11px] text-amber-800">
                  Hinweis: Die KI hat zusätzlich Aufgaben-IDs genannt, die nicht im Raster stehen:{' '}
                  {rubricComputation!.unexpectedTaskIds!.join(', ')}. Diese wurden ignoriert.
                </p>
              ) : null}
              {rubricComputation?.repair?.applied ? (
                <div className="mt-2 rounded border border-emerald-300 bg-emerald-50 px-2 py-1.5 text-[11px] text-emerald-900">
                  <strong>Automatischer Repair-Call erfolgreich.</strong> Die erste KI-Antwort war
                  in sich widersprüchlich (alle Aufgaben auf 0 Punkte, obwohl Stärken/Hinweise
                  konkrete Lösungen nannten). Der Server hat daraufhin gezielt das Punkteraster neu
                  vergeben lassen – die oben angezeigten Punkte stammen aus diesem zweiten Pass.
                </div>
              ) : rubricComputation?.consistencyWarning ===
                  'inconsistent_all_missing_but_feedback_mentions_tasks' &&
                rubricComputation?.repair &&
                rubricComputation.repair.applied === false ? (
                <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
                  <strong>Widersprüchliche KI-Antwort, Repair-Call hat es nicht gelöst.</strong>{' '}
                  Die KI hat jede Aufgabe als „nicht bearbeitet" (0 Punkte) markiert, obwohl in
                  Stärken/Hinweisen konkrete Lösungen stehen. Der automatische zweite Pass hat das
                  ebenfalls nicht korrigiert{' '}
                  {rubricComputation.repair.reason ? (
                    <>
                      (<code>{rubricComputation.repair.reason}</code>)
                    </>
                  ) : null}
                  . Empfehlung: <strong>„Neukorrigieren"</strong> oder – falls das Muster bleibt –
                  die OCR/Bild-Analyse der Abgabe prüfen (möglicherweise ist die Handschrift für
                  das Modell zu schwer lesbar).
                </div>
              ) : rubricComputation?.consistencyWarning ===
                'inconsistent_all_missing_but_feedback_mentions_tasks' ? (
                <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
                  <strong>Widersprüchliche KI-Antwort:</strong> Die KI hat jede Aufgabe als „nicht
                  bearbeitet" (0 Punkte) markiert, schreibt gleichzeitig aber in Stärken/Hinweisen
                  über konkrete Lösungen des Schülers. Empfehlung:{' '}
                  <strong>„Neukorrigieren"</strong> – der Prompt wurde inzwischen präzisiert
                  (klare Definition von „missing", Teilpunkte sind der Normalfall, und bei
                  Widersprüchen läuft automatisch ein Repair-Call).
                </div>
              ) : null}
              {rubricComputation?.reviewBlockReason ? (
                <div className="mt-2 rounded border border-red-300 bg-red-50 px-2 py-1.5 text-[11px] text-red-900">
                  <strong>Widerspruch Vision vs. Korrektur:</strong> Die Bildanalyse hat{' '}
                  {Array.isArray(rubricComputation.conflictTaskIds) && rubricComputation.conflictTaskIds.length > 0 ? (
                    <>
                      die Aufgabe(n){' '}
                      <code>{rubricComputation.conflictTaskIds.join(', ')}</code>
                    </>
                  ) : (
                    'eine oder mehrere Aufgaben'
                  )}{' '}
                  als sichtbar leer erkannt, der Korrektur-LLM hat dort jedoch Punkte vergeben
                  ohne ein wortwörtliches Zitat aus der Schüler-Handschrift zu belegen.
                  <strong> Die Auto-Note wurde deshalb nicht gesetzt.</strong> Bitte die
                  betroffenen Aufgaben im Scan/Foto manuell prüfen und die Note anschließend
                  manuell erfassen oder die Korrektur neu anstoßen.
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          // Platzhalter: kein ausgefülltes Raster und kein Skelett verfügbar. Wir zeigen konkrete
          // Diagnosedaten (Status, Fehler, Artefakt-Inventar) + zwei Aktionen: Raster jetzt ableiten
          // (ohne Referenzversion komplett neu zu bauen) und Korrektur neu starten.
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 px-3 py-2 text-xs text-slate-700">
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold text-slate-800">Korrekturraster für diesen Schüler</div>
              {referenceRubricDiag?.rubricStatus ? (
                <span
                  className={
                    'rounded px-1.5 py-0.5 text-[10px] font-semibold ' +
                    (referenceRubricDiag.rubricStatus === 'failed'
                      ? 'bg-red-100 text-red-800'
                      : referenceRubricDiag.rubricStatus === 'none'
                        ? 'bg-amber-100 text-amber-900'
                        : referenceRubricDiag.rubricStatus === 'ready'
                          ? 'bg-emerald-100 text-emerald-900'
                          : 'bg-slate-200 text-slate-800')
                  }
                  title={`rubric_status = ${referenceRubricDiag.rubricStatus}`}
                >
                  {referenceRubricDiag.rubricStatus}
                </span>
              ) : null}
            </div>
            <div className="mt-1">
              {referenceRubricQuery.isLoading || referenceRubricDiagnosticsQuery.isLoading ? (
                <>Lädt Korrekturraster der Referenz-Version {row.reference_set_version_id ?? '–'} …</>
              ) : referenceRubric && !referenceMatches ? (
                <>
                  Diese Korrektur wurde mit Referenz-Version <strong>{row.reference_set_version_id}</strong> erstellt,
                  aktuell ist aber Version <strong>{primaryReferenceSetVersionId ?? '–'}</strong> aktiv. Das Raster
                  stimmt möglicherweise nicht mehr überein – bitte neu korrigieren.
                </>
              ) : !referenceRubric ? (
                <>
                  Zu Referenz-Version <strong>{row.reference_set_version_id ?? '–'}</strong> liegt kein
                  strukturiertes Korrekturraster vor.
                  {referenceRubricDiag?.recommendation ? (
                    <> {referenceRubricDiag.recommendation}</>
                  ) : (
                    <> Entweder wurde noch keins hinterlegt oder die Raster-Ableitung ist fehlgeschlagen.</>
                  )}
                </>
              ) : (
                <>
                  Diese Korrektur wurde vor Einführung der rasterbasierten Bewertung erstellt. Sie enthält keine
                  Punkte pro Aufgabe. Starte die Korrektur für diese Schülerin/diesen Schüler neu, um die
                  Aufgaben-Punkte zu erhalten.
                </>
              )}
            </div>

            {referenceRubricDiag && !(referenceRubric && referenceMatches) ? (
              <div className="mt-2 rounded border border-slate-200 bg-white/80 px-2 py-1.5 text-[11px] text-slate-800">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5">
                  <div>
                    <span className="text-slate-500">Referenzversion:</span>{' '}
                    <strong>#{referenceRubricDiag.referenceSetVersionId}</strong>
                    {referenceRubricDiag.versionStatus ? (
                      <span className="text-slate-500"> ({referenceRubricDiag.versionStatus})</span>
                    ) : null}
                  </div>
                  <div>
                    <span className="text-slate-500">Raster-Quelle:</span>{' '}
                    {referenceRubricDiag.rubricSource || <em className="text-slate-400">—</em>}
                  </div>
                  <div>
                    <span className="text-slate-500">Ursache:</span>{' '}
                    <strong>{referenceRubricDiag.reason || 'unknown'}</strong>
                  </div>
                  <div>
                    <span className="text-slate-500">Aufgaben im Raster:</span>{' '}
                    {referenceRubricDiag.rubricTaskCount ?? 0}
                  </div>
                </div>
                {referenceRubricDiag.rubricError ? (
                  <div className="mt-1">
                    <span className="text-slate-500">Letzter Fehler:</span>{' '}
                    <code className="break-all rounded bg-red-50 px-1 py-0.5 text-red-800">
                      {referenceRubricDiag.rubricError}
                    </code>
                  </div>
                ) : null}
                {referenceRubricDiag.artifactCounts ? (
                  <div className="mt-1">
                    <span className="text-slate-500">Artefakte:</span>{' '}
                    {Object.entries(referenceRubricDiag.artifactCounts)
                      .filter(([, n]) => Number(n) > 0)
                      .map(([k, n]) => `${k}: ${n}`)
                      .join(' · ') || <em className="text-slate-400">keine</em>}
                    {referenceRubricDiag.anyUsableArtifactContent === false ? (
                      <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900">
                        keine verwertbaren Inhalte (Text/OCR/Vision)
                      </span>
                    ) : null}
                    {(referenceRubricDiag.analysisPendingOrFailedCount || 0) > 0 ? (
                      <span className="ml-2 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-900">
                        {referenceRubricDiag.analysisPendingOrFailedCount} Analyse(n) offen/fehlgeschlagen
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {Array.isArray(referenceRubricDiag.artifacts) && referenceRubricDiag.artifacts.length > 0 ? (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-[11px] text-slate-600 hover:text-slate-900">
                      Artefakte anzeigen ({referenceRubricDiag.artifacts.length})
                    </summary>
                    <ul className="mt-1 space-y-1.5">
                      {referenceRubricDiag.artifacts.map((a) => (
                        <li key={a.id} className="text-[11px] text-slate-700">
                          <div>
                            <span className="font-semibold">{a.artifactType}</span>
                            {a.originalName ? <> · {a.originalName}</> : null}
                            <span className="text-slate-500">
                              {' '}
                              · Text {a.extractedTextLength}, OCR {a.ocrTextLength}
                              {a.hasVisionSummary ? ', Vision' : ''}
                              {a.hasRubricJson ? ', rubric_json' : ''}
                              {a.parseStatus ? ` · parse=${a.parseStatus}` : ''}
                              {a.analysisStatus ? ` · analysis=${a.analysisStatus}` : ''}
                            </span>
                            {a.parseError ? (
                              <span className="ml-1 text-red-700">· {a.parseError}</span>
                            ) : null}
                          </div>
                          {(a.extractedTextSnippet && a.extractedTextSnippet.trim()) ||
                          (a.ocrTextSnippet && a.ocrTextSnippet.trim()) ? (
                            <details className="mt-0.5 ml-1">
                              <summary className="cursor-pointer text-[10px] text-slate-500 hover:text-slate-800">
                                Inhaltsausschnitt anzeigen
                              </summary>
                              {a.extractedTextSnippet && a.extractedTextSnippet.trim() ? (
                                <pre className="mt-0.5 whitespace-pre-wrap rounded border border-slate-200 bg-white p-1.5 text-[10px] text-slate-700">
                                  <strong>Text:</strong> {a.extractedTextSnippet}
                                  {a.extractedTextLength > (a.extractedTextSnippet?.length || 0) ? ' …' : ''}
                                </pre>
                              ) : null}
                              {a.ocrTextSnippet && a.ocrTextSnippet.trim() ? (
                                <pre className="mt-0.5 whitespace-pre-wrap rounded border border-slate-200 bg-white p-1.5 text-[10px] text-slate-700">
                                  <strong>OCR:</strong> {a.ocrTextSnippet}
                                  {a.ocrTextLength > (a.ocrTextSnippet?.length || 0) ? ' …' : ''}
                                </pre>
                              ) : null}
                            </details>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}

                {Array.isArray(referenceRubricDiag.attempts) && referenceRubricDiag.attempts.length > 0 ? (
                  <details className="mt-1" open={referenceRubricDiag.attempts.length <= 2}>
                    <summary className="cursor-pointer text-[11px] font-semibold text-indigo-800 hover:text-indigo-900">
                      Was die KI-Modelle beim letzten Lauf gesagt haben
                      <span className="ml-1 font-normal text-slate-500">
                        ({referenceRubricDiag.attempts.length}{' '}
                        {referenceRubricDiag.attempts.length === 1 ? 'Versuch' : 'Versuche'})
                      </span>
                    </summary>
                    <div className="mt-1 space-y-2">
                      {referenceRubricDiag.attempts.map((att, idx) => {
                        const extractor = att.extractor || 'text';
                        const extractorLabel =
                          extractor === 'vision' ? 'Vision (Bilder/Tabellen)' : 'Text/OCR';
                        const extractorBg =
                          extractor === 'vision' ? 'bg-violet-100 text-violet-900' : 'bg-sky-100 text-sky-900';
                        const artifactsSummary = Array.isArray(att.artifactsSummary)
                          ? att.artifactsSummary
                          : [];
                        const errCombined = String(
                          att.errorCode || att.error || att.reason || ''
                        ).toLowerCase();
                        const isHubBodyOrUpstream =
                          /hub_submit_failed_(413|502|503|504|520|522)|payload too large|body[ _]too[ _]large|request entity too large/i.test(
                            errCombined
                          );
                        const totalBytes =
                          typeof att.totalBytes === 'number' ? att.totalBytes : null;
                        const totalMib =
                          totalBytes != null
                            ? (totalBytes / (1024 * 1024)).toFixed(2)
                            : null;
                        return (
                          <div
                            key={`${extractor}-${idx}`}
                            className="space-y-1 rounded border border-indigo-200 bg-indigo-50/40 p-1.5 text-[11px] text-slate-800"
                          >
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span
                                className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${extractorBg}`}
                              >
                                {extractorLabel}
                              </span>
                              {att.forcedAutogen ? (
                                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900">
                                  autogen erzwungen
                                </span>
                              ) : null}
                              {att.retryFromBodyLimit ? (
                                <span
                                  className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900"
                                  title="Zweiter Versuch mit reduzierter Payload, weil der Hub die große Anfrage abgelehnt hat."
                                >
                                  verkleinert nach 502
                                </span>
                              ) : null}
                              {att.attemptedAt ? (
                                <span className="text-slate-500">
                                  · {new Date(att.attemptedAt).toLocaleString()}
                                </span>
                              ) : null}
                            </div>
                            {isHubBodyOrUpstream ? (
                              <div className="rounded bg-amber-100 px-2 py-1 text-[11px] text-amber-900">
                                <strong>Hub hat die Anfrage abgewiesen (502 / Body-Limit).</strong>{' '}
                                Wahrscheinlichste Ursache: Der Hub-Server hat den Fastify-Default
                                von 1 MB und die multimodale Vision-Anfrage ist mit{' '}
                                {totalMib ? <>ca. <strong>{totalMib} MB</strong></> : 'mehreren Seiten'}{' '}
                                zu groß. Abhilfe: Hub neu deployen – {' '}
                                <code className="rounded bg-white/70 px-1">
                                  backend-core
                                </code>{' '}
                                setzt <code className="rounded bg-white/70 px-1">bodyLimit</code>{' '}
                                jetzt auf 50 MB (Env{' '}
                                <code className="rounded bg-white/70 px-1">
                                  HUB_BODY_LIMIT_BYTES
                                </code>
                                ). Bis dahin greift ein automatischer Retry mit deutlich kleinerer
                                Payload (max. 2 Seiten).
                              </div>
                            ) : null}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5">
                              <div>
                                <span className="text-slate-500">Modus:</span>{' '}
                                <strong>{att.mode || '—'}</strong>
                              </div>
                              <div>
                                <span className="text-slate-500">Modell:</span>{' '}
                                {att.model || <em className="text-slate-400">—</em>}
                              </div>
                              <div>
                                <span className="text-slate-500">Reason:</span>{' '}
                                <strong>{att.reason || '—'}</strong>
                              </div>
                              <div>
                                <span className="text-slate-500">Aufgaben erkannt/normalisiert:</span>{' '}
                                {att.parsedTaskCount ?? '—'} / {att.normalizedTaskCount ?? '—'}
                              </div>
                            </div>
                            {artifactsSummary.length > 0 ? (
                              <div>
                                <span className="text-slate-500">An KI gesendet:</span>{' '}
                                {artifactsSummary
                                  .map((s) => {
                                    const base = `${s.artifactType || '?'}${
                                      s.originalName ? ` "${s.originalName}"` : ''
                                    }`;
                                    if (extractor === 'vision') {
                                      if (s.noUsablePages) return `${base}: keine Seitenbilder`;
                                      const kb =
                                        typeof s.bytesSent === 'number'
                                          ? ` · ${Math.round(s.bytesSent / 1024)} KB`
                                          : '';
                                      return `${base}: ${s.pagesSent ?? 0} Seite${s.pagesSent === 1 ? '' : 'n'}${kb}`;
                                    }
                                    return `${base}: ${s.contentChars ?? 0} Zeichen`;
                                  })
                                  .join(' · ')}
                                {extractor === 'vision' && totalMib ? (
                                  <span className="ml-1 text-slate-500">
                                    (Gesamt ca. <strong>{totalMib} MB</strong>)
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                            {att.priorError ? (
                              <div className="text-[10px] text-amber-900">
                                Vorheriger Versuch:{' '}
                                <code className="break-all rounded bg-amber-50 px-1">
                                  {att.priorError}
                                </code>
                              </div>
                            ) : null}
                            {Array.isArray(att.inventoryErrors) && att.inventoryErrors.length > 0 ? (
                              <div className="text-[10px] text-red-800">
                                Inventory-Fehler:{' '}
                                {att.inventoryErrors
                                  .map(
                                    (e) =>
                                      `Artefakt ${e.artifactId ?? '?'} (${e.stage}): ${e.error}`
                                  )
                                  .join(' · ')}
                              </div>
                            ) : null}
                            {att.notes ? (
                              <div>
                                <span className="text-slate-500">Notes der KI:</span>{' '}
                                <em className="text-slate-700">{att.notes}</em>
                              </div>
                            ) : null}
                            {att.error ? (
                              <div>
                                <span className="text-slate-500">Fehler:</span>{' '}
                                <code className="break-all rounded bg-red-50 px-1 py-0.5 text-red-800">
                                  {att.error}
                                </code>
                              </div>
                            ) : null}
                            {att.rawResponseSnippet ? (
                              <details>
                                <summary className="cursor-pointer text-[10px] text-slate-500 hover:text-slate-800">
                                  Rohe KI-Antwort anzeigen
                                </summary>
                                <pre className="mt-0.5 max-h-48 overflow-auto whitespace-pre-wrap rounded border border-indigo-200 bg-white p-1.5 text-[10px] text-slate-900">
                                  {att.rawResponseSnippet}
                                </pre>
                              </details>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </details>
                ) : null}
              </div>
            ) : null}

            {rebuildLastReason ? (
              <div
                className={
                  'mt-2 rounded px-2 py-1 text-[11px] ' +
                  (rebuildLastReason === 'ready'
                    ? 'bg-emerald-100 text-emerald-900'
                    : rebuildLastReason === 'none'
                      ? 'bg-amber-100 text-amber-900'
                      : 'bg-red-100 text-red-900')
                }
              >
                Raster-Ableitung: <strong>{rebuildLastReason}</strong>
                {rebuildError ? <> · {rebuildError}</> : null}
              </div>
            ) : rebuildError ? (
              <div className="mt-2 rounded bg-red-100 px-2 py-1 text-[11px] text-red-900">{rebuildError}</div>
            ) : null}

            <div className="mt-2 flex flex-wrap gap-2">
              {primaryReferenceSetVersionId ? (
                <>
                  <button
                    type="button"
                    className="rounded border border-indigo-400 bg-white px-2 py-1 text-[11px] font-semibold text-indigo-900 hover:bg-indigo-50 disabled:opacity-60"
                    disabled={rubricRebuildMutation.isLoading}
                    onClick={() => rubricRebuildMutation.mutate({})}
                    title="Führt die Raster-Ableitung für die aktuelle Referenzversion nochmal aus (ohne die Referenzversion komplett neu zu bauen)."
                  >
                    {rubricRebuildMutation.isLoading ? 'Leite Raster ab …' : 'Raster jetzt ableiten'}
                  </button>
                  {referenceRubricDiag?.hasExplicitRubricMaterial ? (
                    <button
                      type="button"
                      className="rounded border border-amber-400 bg-white px-2 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-50 disabled:opacity-60"
                      disabled={rubricRebuildMutation.isLoading}
                      onClick={() => rubricRebuildMutation.mutate({ forceMode: 'autogen' })}
                      title="Ignoriert das hochgeladene Lehrkraft-Raster und leitet die Punkte aus Aufgabenstellung/Musterlösung ab. Das Ergebnis muss danach zur Kenntnis genommen werden."
                    >
                      {rubricRebuildMutation.isLoading
                        ? 'Generiere Raster …'
                        : 'Raster aus Aufgabe/Musterlösung ableiten'}
                    </button>
                  ) : null}
                </>
              ) : null}
              {onRerunCorrection && assignmentId && canRerun ? (
                <button
                  type="button"
                  className="rounded border border-slate-400 bg-white px-2 py-1 text-[11px] font-semibold text-slate-900 hover:bg-slate-100 disabled:opacity-60"
                  disabled={busyAction === 'rerun'}
                  onClick={async () => {
                    try {
                      setBusyAction('rerun');
                      await onRerunCorrection(assignmentId);
                    } finally {
                      setBusyAction(null);
                    }
                  }}
                >
                  {busyAction === 'rerun' ? 'Starte neu …' : 'Korrektur neu starten'}
                </button>
              ) : null}
            </div>
          </div>
        )}
        {mode === 'detail' && st ? (
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Stärken</h4>
            <pre className="whitespace-pre-wrap font-sans text-gray-800 bg-gray-50 border border-gray-100 rounded-md p-3 text-sm">{st}</pre>
          </div>
        ) : null}
        {mode === 'detail' && iss ? (
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Mängel / Hinweise</h4>
            <pre className="whitespace-pre-wrap font-sans text-gray-800 bg-gray-50 border border-gray-100 rounded-md p-3 text-sm">{iss}</pre>
          </div>
        ) : null}
        {mode === 'detail' && sug ? (
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Vorschläge</h4>
            <pre className="whitespace-pre-wrap font-sans text-gray-800 bg-gray-50 border border-gray-100 rounded-md p-3 text-sm">{sug}</pre>
          </div>
        ) : null}
        {mode === 'detail' && competencyAssessment.length > 0 ? (
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Kompetenznachweis (LLM)</h4>
            <div className="space-y-2">
              {competencyAssessment.map((item: any, idx: number) => (
                <div key={`${item?.competency_id || 'comp'}-${item?.competency_stage || 'I'}-${idx}`} className="rounded-md border border-indigo-100 bg-indigo-50/40 px-3 py-2">
                  <div className="text-xs text-indigo-950 font-semibold">
                    Kompetenz #{String(item?.competency_id || '—')} · Stufe {String(item?.competency_stage || 'I')}
                  </div>
                  <div className="text-xs text-indigo-900 mt-0.5">
                    Gezeigt: <span className="font-medium">{String(item?.demonstrated || 'uncertain')}</span>
                    {item?.confidence != null ? <span> · Confidence: {String(item.confidence)}</span> : null}
                  </div>
                  {item?.evidence_quote ? (
                    <pre className="mt-1 whitespace-pre-wrap font-sans text-[12px] text-gray-800 bg-white border border-indigo-100 rounded p-2">
                      {String(item.evidence_quote)}
                    </pre>
                  ) : null}
                  {item?.rationale ? (
                    <p className="mt-1 text-xs text-gray-700">{String(item.rationale)}</p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {mode === 'simple' ? (
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Schülerverständliche Rückmeldung</h4>
            {studentFriendlyStatus === 'queued' || studentFriendlyStatus === 'processing' ? (
              <p className="text-xs text-blue-900 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                Verständlicher Text wird im separaten Hintergrundjob erstellt ({studentFriendlyStatus === 'queued' ? 'wartet' : 'läuft'}).
              </p>
            ) : null}
            {studentFriendlyStatus === 'failed' ? (
              <p className="text-xs text-red-900 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                Verständlicher Text konnte nicht erstellt werden{studentFriendlyError ? `: ${studentFriendlyError}` : '.'}
              </p>
            ) : null}
            {studentFriendlyText ? (
              <pre className="whitespace-pre-wrap font-sans text-gray-800 bg-sky-50 border border-sky-100 rounded-md p-3 text-sm">
                {studentFriendlyText}
              </pre>
            ) : studentFriendlyStatus === 'idle' ? (
              <p className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                Die vereinfachte Rückmeldung wird nach Abschluss der Detailanalyse automatisch erstellt.
              </p>
            ) : null}
          </div>
        ) : null}
        {mode === 'detail' && row.llm_response_json != null && row.llm_response_json !== '' ? (
          <details className="text-xs">
            <summary className="cursor-pointer text-gray-600 hover:text-gray-900">Rohantwort (JSON)</summary>
            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap font-mono bg-slate-900 text-slate-100 rounded-md p-3 text-[11px]">
              {formatLlmField(row.llm_response_json)}
            </pre>
          </details>
        ) : null}
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
            Vollständiger Prompt (Debug, temporär)
          </summary>
          {debugQuery.isLoading ? (
            <p className="mt-2 text-gray-500">Prompt wird geladen …</p>
          ) : promptPretty ? (
            <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap font-mono bg-slate-900 text-emerald-100 rounded-md p-3 text-[11px]">
              {promptPretty}
            </pre>
          ) : (
            <p className="mt-2 text-amber-700">
              Kein Prompt-Log gefunden (Debug-Tabelle/Eintrag fehlt für diesen Lauf).
            </p>
          )}
        </details>
        {(systemPromptPretty || userPayloadPretty) && !debugQuery.isLoading ? (
          <details className="text-xs">
            <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
              An LLM übergeben (System + User Payload)
            </summary>
            {systemPromptPretty ? (
              <div className="mt-2">
                <div className="text-[11px] font-semibold text-gray-700 mb-1">System Prompt</div>
                <pre className="max-h-64 overflow-auto whitespace-pre-wrap font-mono bg-slate-900 text-cyan-100 rounded-md p-3 text-[11px]">
                  {systemPromptPretty}
                </pre>
              </div>
            ) : null}
            {userPayloadPretty ? (
              <div className="mt-2">
                <div className="text-[11px] font-semibold text-gray-700 mb-1">User Payload</div>
                <pre className="max-h-80 overflow-auto whitespace-pre-wrap font-mono bg-slate-900 text-emerald-100 rounded-md p-3 text-[11px]">
                  {userPayloadPretty}
                </pre>
              </div>
            ) : null}
          </details>
        ) : null}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <h3 className="text-lg font-semibold text-gray-900">KI-Korrektur</h3>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5 shadow-sm">
            <button
              type="button"
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${viewMode === 'simple' ? 'bg-sky-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              onClick={() => setViewMode('simple')}
            >
              Vereinfacht
            </button>
            <button
              type="button"
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${viewMode === 'detail' ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              onClick={() => setViewMode('detail')}
            >
              Detail
            </button>
          </div>
          <button
            type="button"
            className="text-xs text-primary-700 hover:underline"
            onClick={() => void q.refetch()}
            disabled={q.isFetching}
          >
            {q.isFetching ? 'Lädt …' : 'Aktualisieren'}
          </button>
          {canRerun ? (
            <button
              type="button"
              className="btn-secondary text-xs"
              disabled={busyAction === 'rerun'}
              onClick={async () => {
                if (!assignmentId || !onRerunCorrection || busyAction) return;
                const ok = window.confirm(
                  'Neukorrigieren starten? Aktive Korrekturergebnisse werden archiviert und bleiben im Verlauf wiederherstellbar.'
                );
                if (!ok) return;
                setBusyAction('rerun');
                try {
                  await onRerunCorrection(assignmentId);
                  await q.refetch();
                } finally {
                  setBusyAction(null);
                }
              }}
            >
              {busyAction === 'rerun' ? 'Startet …' : 'Neukorrigieren'}
            </button>
          ) : null}
        </div>
      </div>
      {subtitle ? <p className="text-sm text-gray-600 mb-3">{subtitle}</p> : null}

      {q.isLoading ? <p className="text-sm text-gray-500">Korrekturen werden geladen …</p> : null}
      {q.isError ? <p className="text-sm text-red-700">Korrekturen konnten nicht geladen werden.</p> : null}

      {!q.isLoading && !q.isError && rows.length === 0 ? (
        <p className="text-sm text-gray-500">Für diese Zuweisung gibt es noch keinen Korrekturlauf.</p>
      ) : null}

      {!q.isLoading && !q.isError && latestCompleted ? (
        <div className="border border-green-100 bg-green-50/40 rounded-lg p-3">{renderBody(latestCompleted, viewMode)}</div>
      ) : null}

      {!q.isLoading && !q.isError && !latestCompleted && latestAny && String(latestAny.status) === 'failed' ? (
        <div className="border border-red-200 bg-red-50 rounded-lg p-3 text-sm text-red-900">
          <div className="font-medium">Letzter Lauf fehlgeschlagen</div>
          {latestAny.error_message ? <p className="mt-2 whitespace-pre-wrap">{latestAny.error_message}</p> : null}
        </div>
      ) : null}

      {!q.isLoading && !q.isError && !latestCompleted && latestAny && !['completed', 'failed'].includes(String(latestAny.status)) ? (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
          Aktueller Status: <strong>{latestAny.status}</strong> – bitte kurz warten oder „Aktualisieren“.
        </p>
      ) : null}

      {!q.isLoading && !q.isError && activeRows.length > 1 ? (
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer text-gray-600 hover:text-gray-900">Aktive Läufe ({activeRows.length})</summary>
          <ul className="mt-2 space-y-2 list-none pl-0">
            {activeRows.map((r) => {
              const pct =
                r.score_percent != null && !Number.isNaN(Number(r.score_percent))
                  ? `${Math.round(Number(r.score_percent))}%`
                  : '—';
              return (
                <li key={r.id} className="border border-gray-100 rounded-md p-2 text-xs text-gray-600">
                  <span className="font-medium text-gray-800">#{r.id}</span> · {r.status} · Prozent {pct}
                  {r.completed_at ? ` · ${new Date(r.completed_at).toLocaleString('de-DE')}` : ''}
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}
      {!q.isLoading && !q.isError && archivedRows.length > 0 ? (
        <details className="mt-4 text-sm" open>
          <summary className="cursor-pointer text-amber-700 hover:text-amber-900">Archivierte Läufe ({archivedRows.length})</summary>
          <p className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
            Diese Läufe sind nicht aktiv, bleiben aber zur Nachvollziehbarkeit erhalten und können wiederhergestellt werden.
          </p>
          <ul className="mt-2 space-y-2 list-none pl-0">
            {archivedRows.map((r) => {
              const restoreBusy = busyAction === `restore:${r.id}`;
              const canRestore = Boolean(assignmentId && onRestoreCorrection && !hasRunningActive && !busyAction);
              const pct = r.score_percent != null && !Number.isNaN(Number(r.score_percent)) ? `${Math.round(Number(r.score_percent))}%` : '—';
              const modelName = modelNameFromRow(r) || '—';
              return (
                <li key={r.id} className="border border-amber-200 bg-amber-50/40 rounded-md p-2 text-xs text-amber-900">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-amber-950">#{r.id}</span>
                    <span>{r.status}</span>
                    <span>· Modell {modelName}</span>
                    <span>· Prozent {pct}</span>
                    {r.completed_at ? <span>· fertig {new Date(r.completed_at).toLocaleString('de-DE')}</span> : null}
                    {r.archived_at ? <span>· archiviert am {new Date(r.archived_at).toLocaleString('de-DE')}</span> : null}
                    <button
                      type="button"
                      className="rounded border border-amber-400 bg-white px-2 py-0.5 font-medium text-amber-900 hover:bg-amber-100"
                      onClick={() => setSelectedArchivedCorrectionId(Number(r.id))}
                    >
                      Anzeigen
                    </button>
                    {onRestoreCorrection ? (
                      <button
                        type="button"
                        className="rounded border border-amber-400 bg-white px-2 py-0.5 font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={!canRestore || restoreBusy}
                        onClick={async () => {
                          if (!assignmentId || !onRestoreCorrection || busyAction) return;
                          const ok = window.confirm(
                            `Archivierten Lauf #${r.id} wiederherstellen? Der aktuell aktive Lauf wird dabei archiviert.`
                          );
                          if (!ok) return;
                          setBusyAction(`restore:${r.id}`);
                          try {
                            await onRestoreCorrection(assignmentId, Number(r.id));
                            await q.refetch();
                          } finally {
                            setBusyAction(null);
                          }
                        }}
                      >
                        {restoreBusy ? 'Stellt wieder her …' : 'Wiederherstellen'}
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
          {selectedArchivedRow ? (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50/60 p-3">
              <div className="mb-2 text-xs font-semibold text-amber-950">
                Archivierter Lauf #{selectedArchivedRow.id}
              </div>
              {renderBody(selectedArchivedRow, viewMode, { showPersistedGrade: false })}
            </div>
          ) : null}
        </details>
      ) : null}
    </div>
  );
}
